/**
 * Active filters indicator showing current filter state with clear options.
 */

import { useCallback, useMemo } from 'react';
import { useFilterStore, ALL_TRANSPORT_MODES } from '../../stores/filterStore';
import type { TransportMode } from '../../types/api';

// Transport mode display names
const MODE_LABELS: Record<TransportMode, string> = {
  tram: 'Tram',
  bus: 'Bus',
  ferry: 'Ferry',
  train: 'Train',
  taxi: 'Taxi',
  teletaxi: 'Flex',
};

export interface ActiveFiltersProps {
  /** Additional CSS class */
  className?: string;
  /** Compact mode - show only when filters are active */
  compact?: boolean;
}

export function ActiveFilters({ className = '', compact = false }: ActiveFiltersProps) {
  const enabledModes = useFilterStore((state) => state.enabledModes);
  const lineFilters = useFilterStore((state) => state.lineFilters);
  const enableAllModes = useFilterStore((state) => state.enableAllModes);
  const clearLineFilters = useFilterStore((state) => state.clearLineFilters);
  const toggleMode = useFilterStore((state) => state.toggleMode);
  const removeLineFilter = useFilterStore((state) => state.removeLineFilter);

  // Calculate which modes are disabled
  const disabledModes = useMemo(() => {
    return ALL_TRANSPORT_MODES.filter((mode) => !enabledModes.has(mode));
  }, [enabledModes]);

  // Check if any filters are active
  const hasLineFilters = lineFilters.length > 0;
  const hasModeFilter = disabledModes.length > 0;
  const hasActiveFilters = hasLineFilters || hasModeFilter;

  // Clear all filters
  const handleClearAll = useCallback(() => {
    enableAllModes();
    clearLineFilters();
  }, [enableAllModes, clearLineFilters]);

  // Re-enable a mode
  const handleEnableMode = useCallback(
    (mode: TransportMode) => {
      toggleMode(mode);
    },
    [toggleMode]
  );

  // Remove a line filter
  const handleRemoveLineFilter = useCallback(
    (line: string) => {
      removeLineFilter(line);
    },
    [removeLineFilter]
  );

  // In compact mode, hide when no filters are active
  if (compact && !hasActiveFilters) {
    return null;
  }

  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-lg shadow-md p-3 ${className}`}
      role="status"
      aria-label="Active filters"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          Active Filters
        </span>
        {hasActiveFilters && (
          <button
            onClick={handleClearAll}
            className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
          >
            Clear All
          </button>
        )}
      </div>

      {!hasActiveFilters ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No active filters</p>
      ) : (
        <div className="space-y-2">
          {/* Line filters */}
          {hasLineFilters && (
            <div className="flex flex-wrap items-center gap-1">
              <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">Lines:</span>
              {lineFilters.map((line) => (
                <button
                  key={line}
                  onClick={() => handleRemoveLineFilter(line)}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 rounded-full text-xs hover:bg-blue-200 dark:hover:bg-blue-800/50 transition-colors"
                  aria-label={`Remove ${line} filter`}
                >
                  {line}
                  <span className="text-blue-600 dark:text-blue-400">×</span>
                </button>
              ))}
            </div>
          )}

          {/* Hidden transport modes */}
          {hasModeFilter && (
            <div className="flex flex-wrap items-center gap-1">
              <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">Hidden:</span>
              {disabledModes.map((mode) => (
                <button
                  key={mode}
                  onClick={() => handleEnableMode(mode)}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full text-xs hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  aria-label={`Show ${MODE_LABELS[mode]}`}
                >
                  {MODE_LABELS[mode]}
                  <span className="text-gray-400 dark:text-gray-500">+</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
